import React, { useState, useMemo } from 'react';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import MiscUtils from 'services/MiscUtils';
import CalendarUtils from 'services/CalendarUtils';
import { useAppSelector } from 'careand-redux/store';

const DEFAULT_ARRAY = [];

// FIB-4 = (Age * AST) / (Platelets * sqrt(ALT))
// AST/ALT in U/L, Platelets in 10^9/L (not absolute count).
// Standard cutoffs: <1.45 low, 1.45-3.25 indeterminate, >3.25 high.
// AGA: for age >=65, low-risk cutoff shifts to 2.0 (3.25 stays).
// https://www.mdcalc.com/calc/2200/fibrosis-4-fib-4-index-liver-fibrosis
export function getCategory(score, age) {
  if (!isFinite(score)) return '';
  const ageNum = parseFloat(age);
  const lowCutoff = ageNum >= 65 ? 2.0 : 1.45;
  if (score < lowCutoff) return 'Low risk for advanced fibrosis';
  if (score <= 3.25) return 'Indeterminate';
  return 'High risk for advanced fibrosis';
}

export function calculateFIB4({ age, ast, alt, platelets }) {
  const a = parseFloat(age);
  const s = parseFloat(ast);
  const l = parseFloat(alt);
  const p = parseFloat(platelets);
  if (!(a > 0 && s > 0 && l > 0 && p > 0)) return NaN;
  return (a * s) / (p * Math.sqrt(l));
}

// Lab values can carry qualifiers (>1000, <5, "12.3 H", "Hemolyzed").
// parseFloat silently truncates "12.3 H" to 12.3 — return null if the
// stringified parse round-trips don't match a clean number, so we can warn
// the practitioner instead of computing on a half-parsed value.
function parseLabValue(raw) {
  if (raw == null || raw === '') return { value: undefined, suspect: false };
  const trimmed = String(raw).trim();
  if (trimmed === '') return { value: undefined, suspect: false };
  const num = parseFloat(trimmed);
  if (!isFinite(num)) return { value: undefined, suspect: true };
  // "12.3" → "12.3", but "12.3 H" or ">1000" survive parseFloat with truncation.
  const suspect = !/^-?\d+(\.\d+)?$/.test(trimmed);
  return { value: trimmed, suspect };
}

const FIB4CalculatorModal = ({ patient, onSubmit }) => {
  const patientSummary =
    useAppSelector((state) => state.healthRecords.byId[patient.id]?.dataSummary) || DEFAULT_ARRAY;

  const { defaultAST, defaultALT, defaultPlatelets, prefillSuspect } = useMemo(() => {
    const findLab = (id) =>
      patientSummary.find(({ metric, origin }) => metric.id === id && origin === 'Lab')?.last_value
        ?.value1;
    const ast = parseLabValue(findLab('AST'));
    const alt = parseLabValue(findLab('ALT'));
    const plt = parseLabValue(findLab('PLATELETS'));
    return {
      defaultAST: ast.value,
      defaultALT: alt.value,
      defaultPlatelets: plt.value,
      prefillSuspect: ast.suspect || alt.suspect || plt.suspect,
    };
  }, [patientSummary]);

  const [age, setAge] = useState(CalendarUtils.calculateAgeNumber(patient.date_of_birth));
  const [ast, setAst] = useState(defaultAST);
  const [alt, setAlt] = useState(defaultALT);
  const [platelets, setPlatelets] = useState(defaultPlatelets);

  const score = calculateFIB4({ age, ast, alt, platelets });
  const result = isFinite(score) ? score.toFixed(2) : '';
  const category = getCategory(score, age);

  // Plausible platelet range is 10-1000 (10^9/L). Anything outside almost
  // certainly means the lab feed gave us an absolute count or a unit mismatch.
  const pltNum = parseFloat(platelets);
  const plateletWarning =
    isFinite(pltNum) && (pltNum < 10 || pltNum > 1000)
      ? 'Platelet value looks implausible — confirm units are x10^9/L (typical range 150-450).'
      : '';

  const ageNum = parseFloat(age);
  const ageWarning =
    isFinite(ageNum) && ageNum < 35
      ? 'FIB-4 is poorly validated below age 35; interpret with caution.'
      : '';

  function getResult() {
    return (
      `FIB-4: ${result} (${category})\n` +
      `Age: ${age}\nAST: ${ast} U/L\nALT: ${alt} U/L\nPlatelets: ${platelets} x10^9/L`
    );
  }

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(getResult());
      MiscUtils.showToastrInfo({ title: 'Copied', message: 'FIB-4 result copied to clipboard.' });
    } catch (err) {
      MiscUtils.showToastrInfo({
        title: 'Copy failed',
        message: 'Could not copy to clipboard. Select and copy the result manually.',
      });
    }
  }

  return (
    <div className="modal">
      <div className="title">FIB-4 Calculator</div>
      <div className="padded-box">
        <div className="row">
          <DefaultTextField
            name="age"
            label="Age (years)"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="platelets"
            label="Platelets (x10^9/L)"
            value={platelets}
            onChange={(e) => setPlatelets(e.target.value)}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="ast"
            label="AST (U/L)"
            autoFocus
            value={ast}
            onChange={(e) => setAst(e.target.value)}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="alt"
            label="ALT (U/L)"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
        </div>
        <div className="row">
          <DefaultTextField
            name="score"
            label="FIB-4"
            value={result}
            disabled
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
        </div>
        {category && (
          <div style={{ marginTop: '10px', fontSize: '13px', lineHeight: 1.5 }}>
            <strong>Interpretation:</strong> {category}
          </div>
        )}
        <div style={{ marginTop: '10px', fontSize: '12px', color: '#666', lineHeight: 1.4 }}>
          Validated for ages 35–65; for age ≥65 the low-risk cutoff is 2.0 instead of 1.45. Not
          validated in acute hepatitis.
        </div>
        {prefillSuspect && (
          <div style={{ marginTop: '6px', fontSize: '12px', color: '#b34700' }}>
            One or more prefilled lab values include qualifiers (e.g. "&gt;", "H"). Confirm the
            numeric values before saving.
          </div>
        )}
        {plateletWarning && (
          <div style={{ marginTop: '6px', fontSize: '12px', color: '#b34700' }}>
            {plateletWarning}
          </div>
        )}
        {ageWarning && (
          <div style={{ marginTop: '6px', fontSize: '12px', color: '#b34700' }}>{ageWarning}</div>
        )}
        <DefaultButton
          name={onSubmit ? 'Submit' : 'Copy to Clipboard'}
          icon={onSubmit ? undefined : 'clipboard'}
          onClick={onSubmit ? () => onSubmit(getResult()) : copyToClipboard}
          style={{ width: '100%', marginTop: '10px' }}
          disabled={!isFinite(score)}
          className="blue"
        />
      </div>
    </div>
  );
};

export default FIB4CalculatorModal;
